import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MqttConnectorService } from '../../mqtt/services/mqtt-connector.service';
import { NotifcationService } from '../../graphql/services/notification.service';
import { AnalyticsService } from './analytics.service';

export type WaterLeakBannerState = 'awaiting_response' | 'shutoff' | 'persistent_wet' | null;

export interface WaterLeakAlert {
  state: WaterLeakBannerState;
  sensorName: string;
  isVisible: boolean;
  notificationId?: string;
}

@Injectable({ providedIn: 'root' })
export class WaterLeakNotificationService {
  private currentAlert = new BehaviorSubject<WaterLeakAlert | null>(null);
  private destroy$ = new Subject<void>();

  /** Banners the resident has closed, so they are not shown again for the same row. */
  private dismissedIds = new Set<string>();

  currentAlert$: Observable<WaterLeakAlert | null> = this.currentAlert.asObservable();

  constructor(
    private ngZone: NgZone,
    private mqtt: MqttConnectorService,
    private notificationService: NotifcationService,
    private analyticsService: AnalyticsService
  ) {}

  init(): void {
    // Listen for water leak notifications via the existing notification subscription.
    // The BehaviorSubject starts empty and populates when initNotifications() is
    // called (by alerts-widget or us). We subscribe first, then trigger a load
    // so the subscription catches the emission.
    this.notificationService.criticalNotifications
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        if (!notifications?.length) return;

        // Find the most recent water leak notification (may not be at index 0
        // due to other critical notifications like wet_sensor_notify)
        //
        // Dismissed rows are filtered out BEFORE the find, not skipped after it.
        // Dismissing does not mark the row read, so it stays in this stream and
        // every later emission would re-pop the banner the resident just closed -
        // and the stream re-emits on any unrelated message being read, so that is
        // not a rare case. Returning early on it instead would let one dismissed
        // row mask every other water-leak row in the list, including a later
        // shutoff or a resolved row that should auto-dismiss the banner.
        const waterLeak = notifications
          .filter(n => !this.dismissedIds.has(n.id))
          .find(n => (n.title || '').toLowerCase().startsWith('water_leak_'));
        if (!waterLeak) return;

        const title = waterLeak.title!.toLowerCase();

        if (title.includes('water_leak_resident_query') && !this.currentAlert.value) {
          this.showAlert({
            state: 'awaiting_response',
            sensorName: this.extractSensorName(waterLeak.text || ''),
            isVisible: true,
            notificationId: waterLeak.id,
          });
        } else if (
          (title.includes('water_leak_shutoff_resident') || title.includes('water_leak_auto_shutoff_resident'))
          // Guarded on THIS row, not on "any banner is up". The row guard stops a
          // shutoff banner re-showing over itself on every emission, which it did
          // before; "any banner" also swallowed the escalation from the unanswered
          // "Is there a leak?" banner to the shutoff one, leaving the resident
          // staring at a question and never told the water had been shut off.
          && this.currentAlert.value?.notificationId !== waterLeak.id
        ) {
          this.showAlert({
            state: 'shutoff',
            sensorName: this.extractSensorName(waterLeak.text || ''),
            isVisible: true,
            notificationId: waterLeak.id,
          });
        } else if (title.includes('water_leak_resolved')) {
          this.dismiss();
        }
      });

    // Trigger notification load in case alerts-widget hasn't mounted yet.
    // Safe to call multiple times — guards against double-subscription internally.
    this.notificationService.initNotifications();
  }

  showAlert(alert: WaterLeakAlert): void {
    this.ngZone.run(() => {
      this.currentAlert.next(alert);
    });
  }

  respondYes(): void {
    this.mqtt.callHaAction('input_select', 'select_option', {
      entity_id: 'input_select.water_leak_state',
      option: 'confirmed_leak',
    }).subscribe();
    this.analyticsService.trackWidgetInteraction('water_leak', 'confirmed_leak');
    this.showAlert({
      ...this.currentAlert.value!,
      state: 'shutoff',
      isVisible: true,
    });
  }

  respondNo(): void {
    this.mqtt.callHaAction('input_select', 'select_option', {
      entity_id: 'input_select.water_leak_state',
      option: 'confirmed_no_leak',
    }).subscribe();
    this.analyticsService.trackWidgetInteraction('water_leak', 'confirmed_no_leak');
    this.showAlert({
      ...this.currentAlert.value!,
      state: 'persistent_wet',
      isVisible: true,
    });
  }

  dismiss(): void {
    const notificationId = this.currentAlert.value?.notificationId;
    if (notificationId) this.dismissedIds.add(notificationId);

    this.ngZone.run(() => {
      this.currentAlert.next(null);
    });
  }

  private extractSensorName(text: string): string {
    // Try to extract sensor name from notification text
    const match = text.match(/(?:at|by|from)\s+(.+?)(?:\.|,|$)/i);
    return match ? match[1].trim() : 'a water sensor';
  }

  cleanup(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
